import type { ElementType } from 'react';

import { cx } from '@hrworks/design-system/functions';
import { ConditionalWrapper } from '@hrworks/sui-shared/components';
import { useResponsiveProp } from '@hrworks/sui-shared/hooks';

import { S } from './Title.styles';
import type { TitleProps } from './Title.types';

export const Title = <C extends ElementType = 'h3'>({
  children,
  titleChildren,
  typography: typographyFromProps,
  uppercase,
  id,
  icon,
  alignTitle,
  breakTitleChildrenWithChildren,
  overflowBehaviour = 'break',
  className,
  lineClamp,
  component,
  ...otherProps
}: TitleProps<C>) => {
  const typography = useResponsiveProp(typographyFromProps) || 'headingMdSemibold';
  const titleChildrenJSX = <S.TitleChildren className={S.cls.TitleChildren}>{titleChildren}</S.TitleChildren>;

  return (
    (children || titleChildren) && (
      <S.TitleContainer
        className={cx(S.cls.TitleContainer, className)}
        typography={typography}
        overflowBehaviour={overflowBehaviour}
        alignTitle={alignTitle}
        {...otherProps}
      >
        {icon}
        {children && (
          <>
            <S.Title
              className={S.cls.Title}
              as={component || 'h3'}
              $overflowBehaviour={overflowBehaviour}
              $uppercase={uppercase}
              $alignTitle={alignTitle}
              $lineClamp={lineClamp}
              id={id}
            >
              <ConditionalWrapper
                condition={!!breakTitleChildrenWithChildren && !!titleChildren}
                wrapper={(children) => <S.PaddedChildren className={S.cls.PaddedChildren}>{children}</S.PaddedChildren>}
              >
                {children}
              </ConditionalWrapper>
              {titleChildren && breakTitleChildrenWithChildren && titleChildrenJSX}
            </S.Title>
            {titleChildren && !breakTitleChildrenWithChildren && titleChildrenJSX}
          </>
        )}
      </S.TitleContainer>
    )
  );
};
